import { Action } from 'redux';

import { Workflow } from 'modules/workflows/types';
import {
  AddEdgeAction,
  AddEdgesAction,
  AddNodeAction,
  AddNodesAction,
  CloneNodesAction,
  DeleteEdgeAction,
  DeleteEdgesAction,
  DeleteNodeAction,
  DeleteNodesAction,
  FetchClonedJourneyResponseAction,
  FetchMetricsResponseAction,
  FetchVisitorCountsResponseAction,
  FetchWorkflowResponseAction,
  JourneyVisitorMetricsResponseAction,
  RemoveClonedJourneyErrorNodeAction,
  SaveWorkflowDraftResponseAction,
  UpdateClonedJourneyErrorNodesAction,
  UpdateHasUnsavedChangesAction,
  UpdateIsAsyncLoadingAction,
  UpdateIsInProgressTotalPublishedViewEnabledAction,
  UpdateIsMetricsSelectionEnabledAction,
  UpdateIsQuietHoursFeatureEnabledAction,
  UpdateIsYesNoSplitDataToggleEnabledAction,
  UpdateNodeAction,
  UpdateNodeDataAction,
  UpdateNodesAction,
  UpdateWorkflowAction,
  UpdateWorkflowMetricsAction,
  UpdateWorkflowStatusResponseAction,
  UpsertWorkflowCampaignLoadingAction,
  WorkflowViewAction,
  actionTypes as t,
} from '../actions';
import { WorkflowState } from '../ducks.types';
import {
  filterWorkflowData,
  getInvalidNodeIds,
  getVisitorCountSum,
  updateJourneyForVisitorMetrics,
} from './utils';
import { WorkflowView } from 'modules/workflows/constants/workflow';

export const workflowReducer = (
  workflow: Workflow | Record<string, never> = {},
  action: Action,
) => {
  switch (action.type) {
    case t.UPDATE_WORKFLOW_METRICS:
    case t.UPDATE_WORKFLOW: {
      const { payload } = action as UpdateWorkflowMetricsAction | UpdateWorkflowAction;
      return {
        ...workflow,
        ...payload.workflow,
      };
    }
    case t.UPDATE_WORKFLOW_STATUS_RESPONSE:
      return {
        ...workflow,
        enabled: (action as UpdateWorkflowStatusResponseAction).payload.enabled,
      };
    case t.SAVE_WORKFLOW_DRAFT_RESPONSE:
    case t.FETCH_WORKFLOW_RESPONSE:
    case t.CREATE_WORKFLOW_DRAFT_RESPONSE: {
      const { payload } = action as FetchWorkflowResponseAction | SaveWorkflowDraftResponseAction;
      return filterWorkflowData(payload.workflow);
    }
    case t.FETCH_JOURNEY_VISITOR_METRICS_RESPONSE: {
      return updateJourneyForVisitorMetrics(
        workflow,
        action as JourneyVisitorMetricsResponseAction,
      );
    }
    case t.FETCH_VISITOR_COUNTS_RESPONSE:
      return {
        ...workflow,
        inProgressCount: getVisitorCountSum(action as FetchVisitorCountsResponseAction),
        InProgressCountRetrievedTimestamp: Date.now(),
      };
    default:
      return workflow;
  }
};

/* -------------------------------------------------------------------------- */
/* Status Of Procesees Within Workflow Studio                                 */
/* -------------------------------------------------------------------------- */

export const fetchingReducer = (state = false, action: Action) => {
  switch (action.type) {
    case t.FETCH_WORKFLOW_RESPONSE:
    case t.FETCH_WORKFLOW_ERROR:
      return false;
    case t.FETCH_WORKFLOW_REQUEST:
      return true;
    default:
      return state;
  }
};

export const hasUnsavedChangesReducer = (state = false, action: Action) => {
  switch (action.type) {
    case t.CREATE_WORKFLOW_DRAFT_RESPONSE:
    case t.SAVE_WORKFLOW_DRAFT_RESPONSE:
      return false;
    case t.ADD_NODE:
    case t.ADD_NODES:
    case t.DELETE_NODE:
    case t.DELETE_NODES:
    case t.UPDATE_NODE:
    case t.UPDATE_NODES:
    case t.UPDATE_NODE_DATA:
    case t.ADD_EDGE:
    case t.ADD_EDGES:
    case t.DELETE_EDGE:
    case t.DELETE_EDGES:
    case t.CLONE_NODES:
    case t.UPDATE_WORKFLOW: {
      const { payload } = action as
        | AddEdgeAction
        | AddEdgesAction
        | AddNodeAction
        | AddNodesAction
        | CloneNodesAction
        | DeleteEdgeAction
        | DeleteEdgesAction
        | DeleteNodeAction
        | DeleteNodesAction
        | UpdateNodeAction
        | UpdateNodesAction
        | UpdateNodeDataAction
        | UpdateWorkflowAction;
      // Passing in `{suppressHasUnsavedChangesUpdate: true}` will prevent stop
      // this property from being updated.
      return payload?.options?.suppressHasUnsavedChangesUpdate === true ? state : true;
    }
    case t.UPDATE_HAS_UNSAVED_CHANGES:
      return (action as UpdateHasUnsavedChangesAction).payload.hasUnsavedChanges;
    default:
      return state;
  }
};

export const savingReducer = (saving = false, action: Action) => {
  switch (action.type) {
    case t.CREATE_WORKFLOW_DRAFT_RESPONSE:
    case t.SAVE_WORKFLOW_DRAFT_RESPONSE:
    case t.RESET_WORKFLOW_DRAFT_ERROR:
    case t.SAVE_WORKFLOW_ERROR:
      return false;
    case t.SAVE_WORKFLOW_DRAFT_REQUEST:
    case t.RESET_WORKFLOW_DRAFT_REQUEST:
      return true;
    default:
      return saving;
  }
};

export const updatingStatusReducer = (state = false, action: Action) => {
  switch (action.type) {
    case t.UPDATE_WORKFLOW_STATUS_RESPONSE:
    case t.UPDATE_WORKFLOW_STATUS_ERROR:
      return false;
    case t.UPDATE_WORKFLOW_STATUS_REQUEST:
      return true;
    default:
      return state;
  }
};

export const isQuietHoursFeatureEnabledReducer = (state = false, action: Action) => {
  switch (action.type) {
    case t.UPDATE_IS_QUIET_HOURS_FEATURE_ENABLED: {
      return (action as UpdateIsQuietHoursFeatureEnabledAction).payload.isEnabled;
    }
    default:
      return state;
  }
};

export const isAsyncLoadingReducer = (state = false, action: Action) => {
  switch (action.type) {
    case t.CREATE_WORKFLOW_DRAFT_RESPONSE:
    case t.RESET_WORKFLOW_DRAFT_ERROR:
      return false;
    case t.RESET_WORKFLOW_DRAFT_REQUEST:
      return true;
    case t.UPDATE_IS_ASYNC_LOADING:
      return (action as UpdateIsAsyncLoadingAction).payload.isLoading;
    default:
      return state;
  }
};

export const isNodeEditorAsyncLoadingReducer = (state = false, action: Action) => {
  switch (action.type) {
    case t.UPSERT_WORKFLOW_CAMPAIGN_LOADING:
      return (action as UpsertWorkflowCampaignLoadingAction).payload.isLoading;
    default:
      return state;
  }
};

export const isYesNoSplitDataToggleEnabledReducer = (state = false, action: Action) => {
  switch (action.type) {
    case t.UPDATE_IS_YES_NO_SPLIT_DATA_TOGGLE_FEATURE_ENABLED: {
      return (action as UpdateIsYesNoSplitDataToggleEnabledAction).payload.isEnabled;
    }
    default:
      return state;
  }
};

export const isMetricsSelectionEnabledReducer = (state = false, action: Action) => {
  switch (action.type) {
    case t.UPDATE_IS_METRICS_SELECTION_ENABLED: {
      return (action as UpdateIsMetricsSelectionEnabledAction).payload.isEnabled;
    }
    default:
      return state;
  }
};

export const isInProgressTotalPublishdViewEnabledReducer = (state = false, action: Action) => {
  switch (action.type) {
    case t.UPDATE_IS_IN_PROGRESS_TOTAL_PUBLISHED_VIEW_ENABLED: {
      return (action as UpdateIsInProgressTotalPublishedViewEnabledAction).payload.isEnabled;
    }
    default:
      return state;
  }
};

export const fetchClonedFromJourneyReducer = (
  clonedFromJourney: WorkflowState['clonedFromJourney'] = {},
  action: Action,
) => {
  switch (action.type) {
    case t.FETCH_CLONED_JOURNEY_RESPONSE:
      return (action as FetchClonedJourneyResponseAction).payload.clonedFromJourney || {};
    case t.FETCH_CLONED_JOURNEY_ERROR:
    case t.FETCH_CLONED_JOURNEY_REQUEST:
      return {};
    case t.UPDATE_CLONED_JOURNEY_ERROR_NODES:
      return {
        ...clonedFromJourney,
        invalidNodes: (action as UpdateClonedJourneyErrorNodesAction).payload.invalidNodes,
      };
    case t.REMOVE_CLONED_JOURNEY_ERROR_NODE:
    case t.DELETE_NODE:
      return {
        ...clonedFromJourney,
        invalidNodes: getInvalidNodeIds(
          [(action as RemoveClonedJourneyErrorNodeAction).payload.nodeId],
          clonedFromJourney?.invalidNodes,
        ),
      };
    case t.DELETE_NODES:
      return {
        ...clonedFromJourney,
        invalidNodes: getInvalidNodeIds(
          (action as DeleteNodesAction).payload.nodeIds,
          clonedFromJourney?.invalidNodes,
        ),
      };
    default:
      return clonedFromJourney;
  }
};

export const flexMetricsReducer = (state: WorkflowState['flexMetrics'] = {}, action: Action) => {
  switch (action.type) {
    case t.FETCH_SEND_METRICS_RESPONSE: {
      return (action as FetchMetricsResponseAction).payload?.dateRange || {};
    }
    default:
      return state;
  }
};

export const workflowViewReducer = (
  state: WorkflowState['workflowView'] = WorkflowView.Total,
  action: Action,
) => {
  switch (action.type) {
    case t.UPDATE_WORKFLOW_VIEW: {
      return (action as WorkflowViewAction)?.payload.workflowView;
    }
    default:
      return state;
  }
};
